/* eslint-disable react/no-unescaped-entities */
import React, { useCallback, useMemo, useRef } from "react";

import { useQuery } from "@apollo/client";
import { Typography, CircularProgress, Box, Container, List, Alert, SxProps, Theme } from "@mui/material";
import { EnergySavingsLeaf } from "@mui/icons-material";
import { uniqBy } from "lodash";

import { USER_WATERINGS } from "api/queries";
import { UserWaterings } from "api/types";

import WateringsListItem from "./WateringsListItem";

const containerStyles: SxProps<Theme> = {
  flexGrow: 1,
  mt: 4,
  textAlign: "center",
};

const titleContainerStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mb: 2,
};

const iconStyles: SxProps<Theme> = {
  mr: 1,
  fontSize: "2rem",
};

const listStyles: SxProps<Theme> = {
  width: "100%",
  bgcolor: "background.paper",
};

const loadingContainerStyles: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  mt: 2,
};

const alertStyles: SxProps<Theme> = {
  mt: 2,
};

type ReportsContentProps = {
  queryDates: { start: string; end: string } | null;
  userId?: string;
};

const WateringsContent = ({ queryDates, userId }: ReportsContentProps) => {
  const observerRef = useRef<IntersectionObserver | null>(null);

  const variables = {
    dateStart: queryDates?.start || "",
    dateEnd: queryDates?.end || "",
    userId,
    offset: 0,
  };

  const { data, loading, error, fetchMore } = useQuery<{
    getUserWaterings: UserWaterings;
  }>(USER_WATERINGS, {
    variables,
    skip: !queryDates,
  });

  const waterings = useMemo(() => data?.getUserWaterings.waterings ?? [], [data]);

  const lastTreeElRef = useCallback(
    (node: Element | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      observerRef.current = new IntersectionObserver(async (entries) => {
        if (entries[0].isIntersecting && waterings.length) {
          try {
            await fetchMore({
              variables: { offset: waterings.length },
              updateQuery: (prev, { fetchMoreResult }) => {
                if (
                  !fetchMoreResult ||
                  (fetchMoreResult?.getUserWaterings && !fetchMoreResult?.getUserWaterings.waterings.length)
                ) {
                  return prev;
                }
                const uniqWaterings = uniqBy(
                  [...prev.getUserWaterings.waterings, ...fetchMoreResult.getUserWaterings.waterings],
                  "id"
                );
                return {
                  getUserWaterings: {
                    ...prev.getUserWaterings,
                    waterings: uniqWaterings,
                  },
                };
              },
            });
          } catch (error) {
            console.log("error: ", error);
          }
        }
      });
      if (node) {
        observerRef.current.observe(node);
      }
    },
    [fetchMore, waterings]
  );

  if (loading)
    return (
      <Box sx={loadingContainerStyles}>
        <CircularProgress />
      </Box>
    );
  if (error)
    return (
      <Alert severity="error" sx={alertStyles}>
        Error: {error.message}
      </Alert>
    );
  if (waterings.length === 0)
    return (
      <Alert severity="warning" sx={alertStyles}>
        No data available for the selected date range.
      </Alert>
    );

  const userName = data?.getUserWaterings.user.name;

  const sortedWaterings = [...waterings].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <Container sx={containerStyles}>
      <Box sx={titleContainerStyles}>
        <EnergySavingsLeaf sx={iconStyles} />
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          {userName}'s Waterings
        </Typography>
      </Box>
      <List sx={listStyles}>
        {sortedWaterings.map((watering) => (
          <WateringsListItem ref={lastTreeElRef} key={watering.id} watering={watering} />
        ))}
      </List>
    </Container>
  );
};

export { WateringsContent };
